import axios from "axios";
import React, { createContext, useContext, useEffect, useState } from "react";
import UserService from "../services/UserService";
import { PolicyEngine } from "../policy-engine/core";
import { NotificationManager } from "react-notifications";
import { publish, subscribe, unsubscribe, unsubscribeAll } from "../utils/events";
import { deepEqual } from "../utils/object";

const AuthContext = createContext({
	signed: false,
	loading: false,
	twoFactorAuthentication: false,
	isSecondFactorAuthenticated: false,
	signIn: Promise,
	signOut: Promise,
	setUser: Promise,
	can: null,
	storeUser: null,
	storeToken: null,
	user: {
		token: "",
		userData: {
			id: 0,
			tipo_usuario: "",
			nome: "",
			mail: "",
			url_avatar: "",
			client: [{}],
			operador: [{}],
		},
	},
});
export const AuthProvider = ({ children }) => {
	const [user, setUser] = useState(null);
	const [accessToken, setAccessToken] = useState(null);
	const [signed, setSigned] = useState(false);
	const [isSecondFactorAuthenticated, setIsSecondFactorAuthenticated] = useState(false);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		loadUserStorageData();
		const storageToken = localStorage.getItem("@BRVAuth:token");
		if (storageToken) {
			setAccessToken(storageToken);
		}
		axios.interceptors.request.use(function (config) {
			if (config.url.includes('s3') && config.url.includes('amazonaws.com')) {
				delete config.headers.Authorization;
				return config;
			}
			config.headers.Authorization = `Bearer ${localStorage.getItem("@BRVAuth:token")}`
			return config;
		});
	}, [])

	async function selectOrganization(organizationId) {
		setLoading(true);
		const response = await UserService.selectOrganization(organizationId);
		setLoading(false);
		storeToken(response.data?.accessToken ?? accessToken)
		if (!response) {
			return;
		}
		setLoading(true);
		const responseUser = await UserService.getUserData();
		setLoading(false);
		storeUser({
			userData: {
				...responseUser.data,
				selectedUnitId: user.userData?.selectedUnitId,
				selectedUnit: user.userData?.selectedUnit,
				selectedOrganizationId: organizationId,
			}
		});
	}

	useEffect(() => {
		function onSseReceived(sseReceived) {
			if (!sseReceived) return;
			if (
				sseReceived.action === 'logoutUser' &&
				user.userData.userExternalId === sseReceived.content.userExternalId
			) {
				signOut();
				NotificationManager.warning(``, `Você foi desconectado pelo motivo: ${sseReceived.content.reason}`, 5000, () => { });
			}
			if (
				sseReceived.action === 'organizationUpdated' &&
				sseReceived.content.organization &&
				user.userData.selectedOrganization.externalId === sseReceived.content.organization.externalId
			) {
				selectOrganization(sseReceived.content.organization.id);
			}
		}
		subscribe('onSseReceived', onSseReceived);
		return () => unsubscribe('onSseReceived', onSseReceived);
	}, [user])

	useEffect(() => {
		loadUserStorageData();
	}, [accessToken]);

	function loadUserStorageData() {
		//armazena usuario e token
		const storageUser = localStorage.getItem("@BRVAuth:user");
		const storageToken = localStorage.getItem("@BRVAuth:token");

		if (storageToken) {
			try {
				const decode = window.atob(storageToken.split(".")[1]);
				if (decode.exp) {
					if (decode.exp * 1000 < new Date().getTime()) {
						setUser(null);
						localStorage.clear();
						window.location.replace("/login");
					}
				}
			} catch (error) {
				console.error("Error decoding token:", error);
				// Handle error, e.g., clear localStorage and redirect to login
				setUser(null);
				localStorage.clear();
				window.location.replace("/login");
			}
		}

		//se tiver o token atualiza header de autenticação
		if (storageToken && storageUser) {

			//atualiza state com dados de usuario e token encontrados
			const data = {
				userData: JSON.parse(storageUser),
			};
			storeUser(data, true);
			setLoading(false);
		}
	}

	async function signIn(mail, password) {
		//realiza uma chamada HTTP para realização do login
		//atualiza header de autenticação com a response retornada do backend
		const response = await UserService.login(mail, password);

		if ('mustResetPassword' in response.data) {
			NotificationManager.error("", "Você precisa redefinir sua senha para entrar na plataforma. Por favor verifique seu email para obter o link.", 5000, () => {
				alert("callback");
			});
		}

		if (response.status !== 200) return;

		setIsSecondFactorAuthenticated(false)

		//atualiza storage com o novo token
		storeToken(response.data.token);

		if (response.data.userData?.twoFactorAuthentication) {
			return response.data;
		}

		storeUser(response.data);

		return response.data;
	}

	function storeToken(token) {
		if (accessToken === token) return;
		localStorage.setItem("@BRVAuth:token", token);
		setAccessToken(token);
	}

	function storeUser(data, onlyState = false) {
		//atualiza state com dados da response
		setUser(prevUser => {
			if (deepEqual(prevUser, data)) return prevUser;
			if (!onlyState) {
				localStorage.setItem(
					"@BRVAuth:user",
					JSON.stringify(data.userData),
				);
				setSigned(true);
			}
			return data;
		});
	}

	function isLogged() {
		return localStorage.getItem('@BRVAuth:token') && localStorage.getItem('@BRVAuth:user');
	}

	function can(actions) {
		if (!user) return false;
		const policyEngine = PolicyEngine.init(user.userData, user.userData.policies, user.userData.scopedActions);
		return policyEngine.can(actions);
	}

	function signOut() {
		setUser(null);
		setSigned(false);
		delete axios.defaults.headers.Authorization;
		localStorage.clear();
		publish('logout');
	}

	return (
		//fornece os metodos e variaveis de login para o componente filho
		<AuthContext.Provider
			value={{ signed, loading, isLogged, user, can, setUser, signIn, signOut, storeToken, storeUser, accessToken }}
		>
			{children}
		</AuthContext.Provider>
	);
};

export function useAuth() {
	const context = useContext(AuthContext);

	return context;
}
